import { call, put, takeLatest } from "redux-saga/effects";
import Swal from "sweetalert2";
import { extractErrorMessage } from "../../../api";
import { performanceApi } from "../api/performanceApi";
import * as actions from "../actions/performanceActions";

const dataOf = (response) => response?.data ?? null;
const errorOf = (error, fallback) => {
    const details = error?.response?.data?.details;
    if (Array.isArray(details) && details.length) {
        const joined = details.join(" ");
        if (joined.toLowerCase().includes("poids")) return "Poids nul, veuillez le saisir.";
        if (joined.toLowerCase().includes("justification")) return "Veuillez remplir la justification.";
        return details.join("\n");
    }
    return extractErrorMessage(error, fallback);
};

function* refreshStepper(progressId) {
    if (!progressId) return;
    try {
        const response = yield call(performanceApi.fetchStepper, progressId);
        yield put({
            type: actions.FETCH_PERFORMANCE_STEPPER_SUCCESS,
            payload: { progressId, data: dataOf(response) },
        });
    } catch (error) {
        yield put({
            type: actions.FETCH_PERFORMANCE_STEPPER_FAIL,
            payload: { progressId, error: errorOf(error, "Erreur chargement stepper") },
        });
    }
}

function* refreshObjectifsN(progressId) {
    if (!progressId) return;
    const response = yield call(performanceApi.fetchObjectifsN, progressId);
    yield put({ type: actions.FETCH_OBJECTIFS_N_SUCCESS, payload: { progressId, data: dataOf(response) } });
}

function* refreshObjectifsNMoins1(progressId) {
    if (!progressId) return;
    const response = yield call(performanceApi.fetchObjectifsNMoins1Step, progressId);
    yield put({ type: actions.FETCH_OBJECTIFS_N_MOINS_1_STEP_SUCCESS, payload: { progressId, data: dataOf(response) } });
}

function* refreshCompetencesGeneriques(progressId) {
    if (!progressId) return;
    const response = yield call(performanceApi.fetchCompetencesGeneriquesStep, progressId);
    yield put({ type: actions.FETCH_COMPETENCES_GENERIQUES_STEP_SUCCESS, payload: { progressId, data: dataOf(response) } });
}

function* refreshCompetencesSpecifiques(progressId) {
    if (!progressId) return;
    const response = yield call(performanceApi.fetchCompetencesSpecifiquesStep, progressId);
    yield put({ type: actions.FETCH_COMPETENCES_SPECIFIQUES_STEP_SUCCESS, payload: { progressId, data: dataOf(response) } });
}

function* refreshPlanDeveloppement(progressId) {
    if (!progressId) return;
    const response = yield call(performanceApi.fetchPlanDeveloppementStep, progressId);
    yield put({ type: actions.FETCH_PLAN_DEVELOPPEMENT_STEP_SUCCESS, payload: { progressId, data: dataOf(response) } });
}

function* refreshEntretien(progressId) {
    if (!progressId) return;
    const response = yield call(performanceApi.fetchEntretienAnnuel, progressId);
    yield put({ type: actions.FETCH_ENTRETIEN_ANNUEL_SUCCESS, payload: { progressId, data: dataOf(response) } });
}

function* fetchCampaignCardsSaga() {
    try {
        const response = yield call(performanceApi.fetchCampaignCards);
        yield put({ type: actions.FETCH_PERFORMANCE_CAMPAIGN_CARDS_SUCCESS, payload: { data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_PERFORMANCE_CAMPAIGN_CARDS_FAIL, payload: { error: errorOf(error, "Erreur chargement campagnes performance") } });
    }
}

function* startProgressSaga(action) {
    const { campagneId, onSuccess } = action.payload;
    try {
        const response = yield call(performanceApi.startProgress, campagneId);
        const data = dataOf(response);
        yield put({ type: actions.START_PERFORMANCE_PROGRESS_SUCCESS, payload: { data } });
        yield call(refreshStepper, data?.id);
        if (typeof onSuccess === "function") {
            yield call(onSuccess, data);
        }
    } catch (error) {
        yield put({ type: actions.START_PERFORMANCE_PROGRESS_FAIL, payload: { error: errorOf(error, "Erreur creation progression performance") } });
    }
}

function* fetchStepperSaga(action) {
    const { progressId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchStepper, progressId);
        yield put({ type: actions.FETCH_PERFORMANCE_STEPPER_SUCCESS, payload: { progressId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_PERFORMANCE_STEPPER_FAIL, payload: { progressId, error: errorOf(error, "Erreur chargement stepper") } });
    }
}

function* fetchObjectifsNMoins1StepSaga(action) {
    const { progressId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchObjectifsNMoins1Step, progressId);
        yield put({ type: actions.FETCH_OBJECTIFS_N_MOINS_1_STEP_SUCCESS, payload: { progressId, data: dataOf(response) } });
        yield call(refreshStepper, progressId);
    } catch (error) {
        yield put({ type: actions.FETCH_OBJECTIFS_N_MOINS_1_STEP_FAIL, payload: { progressId, error: errorOf(error, "Erreur chargement objectifs N-1") } });
    }
}

function* createObjectifRevisionSaga(action, apiMethod, successType, failType, fallback) {
    const { objectifId, data } = action.payload;
    try {
        const response = yield call(apiMethod, objectifId, data);
        const responseData = dataOf(response);
        const progressId = responseData?.evaluationProgressId || data?.evaluationProgressId;
        yield put({ type: successType, payload: { progressId, data: responseData } });
        yield call(refreshObjectifsNMoins1, progressId);
        yield call(refreshStepper, progressId);
    } catch (error) {
        yield put({ type: failType, payload: { progressId: data?.evaluationProgressId, error: errorOf(error, fallback) } });
    }
}

function* createObjectifNMoins1Saga(action) {
    const { progressId, data } = action.payload;
    try {
        const response = yield call(performanceApi.createObjectifNMoins1, progressId, data);
        const responseData = dataOf(response);
        yield put({ type: actions.CREATE_OBJECTIF_N_MOINS_1_SUCCESS, payload: { progressId, data: responseData } });
        yield call(refreshObjectifsNMoins1, progressId);
        yield call(refreshStepper, progressId);
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Objectif N-1 ajouté avec succès",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (error) {
        const msg = errorOf(error, "Erreur creation objectif N-1");
        yield put({ type: actions.CREATE_OBJECTIF_N_MOINS_1_FAIL, payload: { progressId, error: msg } });
        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: msg,
            confirmButtonText: "Compris",
        });
    }
}

function* fetchObjectifsNSaga(action) {
    const { progressId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchObjectifsN, progressId);
        yield put({ type: actions.FETCH_OBJECTIFS_N_SUCCESS, payload: { progressId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_OBJECTIFS_N_FAIL, payload: { progressId, error: errorOf(error, "Erreur chargement objectifs N") } });
    }
}

function* mutateObjectifSaga(action, apiMethod, successType, failType, fallback) {
    const { objectifId, data, progressId: payloadProgressId, refreshNMoins1 } = action.payload;
    try {
        const response = data === undefined
            ? yield call(apiMethod, objectifId)
            : yield call(apiMethod, objectifId, data);
        const responseData = dataOf(response);
        const progressId = payloadProgressId || responseData?.evaluationProgressId || data?.evaluationProgressId;
        yield put({ type: successType, payload: { progressId, data: responseData } });
        if (refreshNMoins1) {
            yield call(refreshObjectifsNMoins1, progressId);
        } else {
            yield call(refreshObjectifsN, progressId);
        }
        yield call(refreshStepper, progressId);
        if (successType === actions.UPDATE_OBJECTIF_SUCCESS) {
            yield call([Swal, Swal.fire], {
                icon: "success",
                text: "Objectif modifie avec succes",
                timer: 1500,
                showConfirmButton: false,
            });
        }
    } catch (error) {
        const msg = errorOf(error, fallback);
        yield put({ type: failType, payload: { progressId: payloadProgressId || data?.evaluationProgressId, error: msg } });
        if (failType === actions.UPDATE_OBJECTIF_FAIL) {
            yield call([Swal, Swal.fire], {
                icon: "error",
                title: "Modification non enregistree",
                text: msg,
                confirmButtonText: "Compris",
            });
        }
    }
}

function* createObjectifSaga(action) {
    const { data } = action.payload;
    try {
        const response = yield call(performanceApi.createObjectif, data);
        const responseData = dataOf(response);
        const progressId = responseData?.evaluationProgressId || data?.evaluationProgressId;
        yield put({ type: actions.CREATE_OBJECTIF_SUCCESS, payload: { progressId, data: responseData } });
        yield call(refreshObjectifsN, progressId);
        yield call(refreshStepper, progressId);

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Objectif ajouté avec succès",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (error) {
        const msg = errorOf(error, "Erreur creation objectif");
        yield put({ type: actions.CREATE_OBJECTIF_FAIL, payload: { progressId: data?.evaluationProgressId, error: msg } });

        yield call([Swal, Swal.fire], {
            icon: "error",
            text: msg,
            title: "Erreur",
            confirmButtonText: "Compris",
        });
    }
}

function* fetchModificationRequestsSaga(action) {
    const { objectifId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchObjectifModificationRequests, objectifId);
        yield put({ type: actions.FETCH_OBJECTIF_MODIFICATION_REQUESTS_SUCCESS, payload: { objectifId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_OBJECTIF_MODIFICATION_REQUESTS_FAIL, payload: { objectifId, error: errorOf(error, "Erreur chargement demandes de modification") } });
    }
}

function* fetchProgressModificationRequestsSaga(action) {
    const { progressId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchProgressObjectifModificationRequests, progressId);
        yield put({ type: actions.FETCH_PROGRESS_OBJECTIF_MODIFICATION_REQUESTS_SUCCESS, payload: { progressId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_PROGRESS_OBJECTIF_MODIFICATION_REQUESTS_FAIL, payload: { progressId, error: errorOf(error, "Erreur chargement historique des demandes") } });
    }
}

function* fetchManagerPendingModificationsSaga() {
    try {
        const response = yield call(performanceApi.fetchManagerPendingObjectifModificationRequests);
        yield put({ type: actions.FETCH_MANAGER_PENDING_OBJECTIF_MODIFICATIONS_SUCCESS, payload: { data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_MANAGER_PENDING_OBJECTIF_MODIFICATIONS_FAIL, payload: { error: errorOf(error, "Erreur chargement demandes manager") } });
    }
}

function* mutateModificationSaga(action, apiMethod, successType, failType, fallback) {
    const { objectifId, requestId, data, progressId: payloadProgressId, refreshNMoins1 } = action.payload;
    try {
        let response;
        if (objectifId) {
            response = yield call(apiMethod, objectifId, data);
        } else {
            response = yield call(apiMethod, requestId, data);
        }
        const responseData = dataOf(response);
        const progressId = refreshNMoins1
            ? payloadProgressId
            : responseData?.objectif?.evaluationProgressId || responseData?.evaluationProgressId || data?.evaluationProgressId || payloadProgressId;
        const resolvedObjectifId = responseData?.objectifId || objectifId;
        yield put({ type: successType, payload: { objectifId: resolvedObjectifId, progressId, data: responseData } });
        if (refreshNMoins1) {
            yield call(refreshObjectifsNMoins1, progressId);
        } else {
            yield call(refreshObjectifsN, progressId);
        }
        if (resolvedObjectifId) {
            yield call(fetchModificationRequestsSaga, { payload: { objectifId: resolvedObjectifId } });
        }
        yield call(fetchManagerPendingModificationsSaga);
        yield call(refreshStepper, progressId);
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: successType === actions.SUBMIT_OBJECTIF_MODIFICATION_SUCCESS
                ? "Demande envoyee au manager"
                : "Demande traitee avec succes",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (error) {
        const msg = errorOf(error, fallback);
        yield put({ type: failType, payload: { objectifId, progressId: payloadProgressId, error: msg } });
        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: msg,
            confirmButtonText: "Compris",
        });
    }
}

function* fetchCompetencesGeneriquesStepSaga(action) {
    const { progressId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchCompetencesGeneriquesStep, progressId);
        yield put({ type: actions.FETCH_COMPETENCES_GENERIQUES_STEP_SUCCESS, payload: { progressId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_COMPETENCES_GENERIQUES_STEP_FAIL, payload: { progressId, error: errorOf(error, "Erreur chargement competences generiques") } });
    }
}

function* mutateCompetenceGeneriqueSaga(action, apiMethod, successType, failType, fallback) {
    const { data } = action.payload;
    try {
        const response = yield call(apiMethod, data);
        const responseData = dataOf(response);
        const progressId = responseData?.evaluationProgressId || data?.evaluationProgressId;
        yield put({ type: successType, payload: { progressId, data: responseData } });
        yield call(refreshCompetencesGeneriques, progressId);
        yield call(refreshStepper, progressId);
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: successType === actions.SAVE_COMPETENCE_GENERIQUE_MANAGER_EVAL_SUCCESS
                ? "Evaluation validee par le manager"
                : "Evaluation enregistree avec succes",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (error) {
        const msg = errorOf(error, fallback);
        yield put({ type: failType, payload: { progressId: data?.evaluationProgressId, error: msg } });
        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: msg,
            confirmButtonText: "Compris",
        });
    }
}

function* fetchCompetencesSpecifiquesStepSaga(action) {
    const { progressId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchCompetencesSpecifiquesStep, progressId);
        yield put({ type: actions.FETCH_COMPETENCES_SPECIFIQUES_STEP_SUCCESS, payload: { progressId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_COMPETENCES_SPECIFIQUES_STEP_FAIL, payload: { progressId, error: errorOf(error, "Erreur chargement competences specifiques") } });
    }
}

function* mutateCompetenceSpecifiqueSaga(action, apiMethod, successType, failType, fallback) {
    const { data } = action.payload;
    try {
        const response = yield call(apiMethod, data);
        const responseData = dataOf(response);
        const progressId = responseData?.evaluationProgressId || data?.evaluationProgressId;
        yield put({ type: successType, payload: { progressId, data: responseData } });
        yield call(refreshCompetencesSpecifiques, progressId);
        yield call(refreshPlanDeveloppement, progressId);
        yield call(refreshStepper, progressId);
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: successType === actions.SAVE_COMPETENCE_SPECIFIQUE_MANAGER_EVAL_SUCCESS
                ? "Competence specifique validee par le manager"
                : "Auto-evaluation enregistree avec succes",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (error) {
        const msg = errorOf(error, fallback);
        yield put({ type: failType, payload: { progressId: data?.evaluationProgressId, error: msg } });
        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: msg,
            confirmButtonText: "Compris",
        });
    }
}

function* mutateCompetencesBulkSaga(action, apiMethod, successType, failType, refreshStep, successMessage, fallback, refreshPlan = false) {
    const { progressId, items = [] } = action.payload;
    try {
        if (!items.length) {
            yield call([Swal, Swal.fire], {
                icon: "info",
                text: "Aucune competence a enregistrer",
                timer: 1500,
                showConfirmButton: false,
            });
            return;
        }
        let lastResponseData = null;
        for (const item of items) {
            const response = yield call(apiMethod, item);
            lastResponseData = dataOf(response);
        }
        yield put({ type: successType, payload: { progressId, data: lastResponseData, count: items.length } });
        yield call(refreshStep, progressId);
        if (refreshPlan) {
            yield call(refreshPlanDeveloppement, progressId);
        }
        yield call(refreshStepper, progressId);
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: successMessage,
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (error) {
        const msg = errorOf(error, fallback);
        yield put({ type: failType, payload: { progressId, error: msg } });
        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: msg,
            confirmButtonText: "Compris",
        });
    }
}

function* fetchPlanDeveloppementStepSaga(action) {
    const { progressId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchPlanDeveloppementStep, progressId);
        yield put({ type: actions.FETCH_PLAN_DEVELOPPEMENT_STEP_SUCCESS, payload: { progressId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_PLAN_DEVELOPPEMENT_STEP_FAIL, payload: { progressId, error: errorOf(error, "Erreur chargement plan de developpement") } });
    }
}

function* mutatePlanDeveloppementSaga(action, apiMethod, successType, failType, fallback) {
    const { progressId, data } = action.payload;
    try {
        let response;
        if (progressId) {
            response = yield call(apiMethod, progressId);
        } else {
            response = yield call(apiMethod, data);
        }
        const responseData = dataOf(response);
        const resolvedProgressId = responseData?.evaluationProgressId || progressId || data?.evaluationProgressId;
        yield put({ type: successType, payload: { progressId: resolvedProgressId, data: responseData } });
        yield call(refreshPlanDeveloppement, resolvedProgressId);
        yield call(refreshStepper, resolvedProgressId);
    } catch (error) {
        yield put({ type: failType, payload: { progressId: progressId || data?.evaluationProgressId, error: errorOf(error, fallback) } });
    }
}

function* fetchEntretienAnnuelSaga(action) {
    const { progressId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchEntretienAnnuel, progressId);
        yield put({ type: actions.FETCH_ENTRETIEN_ANNUEL_SUCCESS, payload: { progressId, data: dataOf(response) } });
    } catch (error) {
        const status = error?.response?.status;
        yield put({
            type: actions.FETCH_ENTRETIEN_ANNUEL_FAIL,
            payload: { progressId, error: status === 204 ? null : errorOf(error, "Erreur chargement entretien annuel") },
        });
    }
}

function* mutateEntretienSaga(action, apiMethod, successType, failType, fallback) {
    const { entretienId, data } = action.payload;
    try {
        const response = entretienId ? yield call(apiMethod, entretienId, data) : yield call(apiMethod, data);
        const responseData = dataOf(response);
        const progressId = responseData?.evaluationProgressId || data?.evaluationProgressId;
        yield put({ type: successType, payload: { progressId, data: responseData } });
        yield call(refreshEntretien, progressId);
        yield call(refreshStepper, progressId);
    } catch (error) {
        yield put({ type: failType, payload: { progressId: data?.evaluationProgressId, error: errorOf(error, fallback) } });
    }
}

function* fetchManagerDashboardSaga(action) {
    const { campagneId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchManagerPerformanceDashboard, campagneId);
        yield put({ type: actions.FETCH_MANAGER_PERFORMANCE_DASHBOARD_SUCCESS, payload: { campagneId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_MANAGER_PERFORMANCE_DASHBOARD_FAIL, payload: { campagneId, error: errorOf(error, "Erreur chargement dashboard manager") } });
    }
}

function* fetchCampagneConsolidationSaga(action) {
    const { campagneId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchCampagneConsolidation, campagneId);
        yield put({ type: actions.FETCH_CONSOLIDATION_CAMPAGNE_SUCCESS, payload: { campagneId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_CONSOLIDATION_CAMPAGNE_FAIL, payload: { campagneId, error: errorOf(error, "Erreur chargement consolidation") } });
    }
}

function* fetchProgressConsolidationSaga(action) {
    const { progressId } = action.payload;
    try {
        const response = yield call(performanceApi.fetchProgressConsolidation, progressId);
        yield put({ type: actions.FETCH_PROGRESS_CONSOLIDATION_SUCCESS, payload: { progressId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.FETCH_PROGRESS_CONSOLIDATION_FAIL, payload: { progressId, error: errorOf(error, "Erreur chargement synthese collaborateur") } });
    }
}

function* recalculateCampagneConsolidationSaga(action) {
    const { campagneId } = action.payload;
    try {
        const response = yield call(performanceApi.recalculateCampagneConsolidation, campagneId);
        yield put({ type: actions.RECALCULATE_CONSOLIDATION_CAMPAGNE_SUCCESS, payload: { campagneId, data: dataOf(response) } });
    } catch (error) {
        yield put({ type: actions.RECALCULATE_CONSOLIDATION_CAMPAGNE_FAIL, payload: { campagneId, error: errorOf(error, "Erreur recalcul consolidation") } });
    }
}

function* recalculateProgressConsolidationSaga(action) {
    const { progressId } = action.payload;
    try {
        const response = yield call(performanceApi.recalculateProgressConsolidation, progressId);
        yield put({ type: actions.RECALCULATE_PROGRESS_CONSOLIDATION_SUCCESS, payload: { progressId, data: dataOf(response) } });
        yield call(refreshStepper, progressId);
    } catch (error) {
        yield put({ type: actions.RECALCULATE_PROGRESS_CONSOLIDATION_FAIL, payload: { progressId, error: errorOf(error, "Erreur recalcul synthese collaborateur") } });
    }
}

export default function* performanceSaga() {
    yield takeLatest(actions.FETCH_PERFORMANCE_CAMPAIGN_CARDS_REQUEST, fetchCampaignCardsSaga);
    yield takeLatest(actions.START_PERFORMANCE_PROGRESS_REQUEST, startProgressSaga);
    yield takeLatest(actions.FETCH_PERFORMANCE_STEPPER_REQUEST, fetchStepperSaga);

    yield takeLatest(actions.FETCH_OBJECTIFS_N_MOINS_1_STEP_REQUEST, fetchObjectifsNMoins1StepSaga);
    yield takeLatest(actions.CREATE_OBJECTIF_REVISION_REQUEST, function* (action) {
        yield call(createObjectifRevisionSaga, action, performanceApi.createObjectifRevision, actions.CREATE_OBJECTIF_REVISION_SUCCESS, actions.CREATE_OBJECTIF_REVISION_FAIL, "Erreur revision objectif");
    });
    yield takeLatest(actions.CREATE_OBJECTIF_AUTO_EVALUATION_REQUEST, function* (action) {
        yield call(createObjectifRevisionSaga, action, performanceApi.createObjectifAutoEvaluation, actions.CREATE_OBJECTIF_AUTO_EVALUATION_SUCCESS, actions.CREATE_OBJECTIF_AUTO_EVALUATION_FAIL, "Erreur auto-evaluation objectif");
    });
    yield takeLatest(actions.CREATE_OBJECTIF_MANAGER_EVALUATION_REQUEST, function* (action) {
        yield call(createObjectifRevisionSaga, action, performanceApi.createObjectifManagerEvaluation, actions.CREATE_OBJECTIF_MANAGER_EVALUATION_SUCCESS, actions.CREATE_OBJECTIF_MANAGER_EVALUATION_FAIL, "Erreur evaluation manager objectif");
    });
    yield takeLatest(actions.CREATE_OBJECTIF_N_MOINS_1_REQUEST, createObjectifNMoins1Saga);

    yield takeLatest(actions.FETCH_OBJECTIFS_N_REQUEST, fetchObjectifsNSaga);
    yield takeLatest(actions.CREATE_OBJECTIF_REQUEST, createObjectifSaga);
    yield takeLatest(actions.UPDATE_OBJECTIF_REQUEST, function* (action) {
        yield call(mutateObjectifSaga, action, performanceApi.updateObjectif, actions.UPDATE_OBJECTIF_SUCCESS, actions.UPDATE_OBJECTIF_FAIL, "Erreur modification objectif");
    });
    yield takeLatest(actions.DELETE_OBJECTIF_REQUEST, function* (action) {
        yield call(mutateObjectifSaga, action, performanceApi.deleteObjectif, actions.DELETE_OBJECTIF_SUCCESS, actions.DELETE_OBJECTIF_FAIL, "Erreur suppression objectif");
    });
    yield takeLatest(actions.APPROVE_OBJECTIF_REQUEST, function* (action) {
        yield call(mutateObjectifSaga, action, performanceApi.approveObjectif, actions.APPROVE_OBJECTIF_SUCCESS, actions.APPROVE_OBJECTIF_FAIL, "Erreur approbation objectif");
    });
    yield takeLatest(actions.REJECT_OBJECTIF_REQUEST, function* (action) {
        yield call(mutateObjectifSaga, action, performanceApi.rejectObjectif, actions.REJECT_OBJECTIF_SUCCESS, actions.REJECT_OBJECTIF_FAIL, "Erreur rejet objectif");
    });

    yield takeLatest(actions.FETCH_OBJECTIF_MODIFICATION_REQUESTS_REQUEST, fetchModificationRequestsSaga);
    yield takeLatest(actions.FETCH_PROGRESS_OBJECTIF_MODIFICATION_REQUESTS_REQUEST, fetchProgressModificationRequestsSaga);
    yield takeLatest(actions.FETCH_MANAGER_PENDING_OBJECTIF_MODIFICATIONS_REQUEST, fetchManagerPendingModificationsSaga);
    yield takeLatest(actions.SUBMIT_OBJECTIF_MODIFICATION_REQUEST, function* (action) {
        yield call(mutateModificationSaga, action, performanceApi.submitObjectifModification, actions.SUBMIT_OBJECTIF_MODIFICATION_SUCCESS, actions.SUBMIT_OBJECTIF_MODIFICATION_FAIL, "Erreur demande de modification");
    });
    yield takeLatest(actions.APPROVE_OBJECTIF_MODIFICATION_REQUEST, function* (action) {
        yield call(mutateModificationSaga, action, performanceApi.approveObjectifModification, actions.APPROVE_OBJECTIF_MODIFICATION_SUCCESS, actions.APPROVE_OBJECTIF_MODIFICATION_FAIL, "Erreur approbation modification");
    });
    yield takeLatest(actions.REJECT_OBJECTIF_MODIFICATION_REQUEST, function* (action) {
        yield call(mutateModificationSaga, action, performanceApi.rejectObjectifModification, actions.REJECT_OBJECTIF_MODIFICATION_SUCCESS, actions.REJECT_OBJECTIF_MODIFICATION_FAIL, "Erreur refus modification");
    });
    yield takeLatest(actions.CANCEL_OBJECTIF_MODIFICATION_REQUEST, function* (action) {
        yield call(mutateModificationSaga, action, performanceApi.cancelObjectifModification, actions.CANCEL_OBJECTIF_MODIFICATION_SUCCESS, actions.CANCEL_OBJECTIF_MODIFICATION_FAIL, "Erreur annulation modification");
    });

    yield takeLatest(actions.FETCH_COMPETENCES_GENERIQUES_STEP_REQUEST, fetchCompetencesGeneriquesStepSaga);
    yield takeLatest(actions.SAVE_COMPETENCE_GENERIQUE_AUTO_EVAL_REQUEST, function* (action) {
        yield call(mutateCompetenceGeneriqueSaga, action, performanceApi.saveCompetenceGeneriqueAutoEvaluation, actions.SAVE_COMPETENCE_GENERIQUE_AUTO_EVAL_SUCCESS, actions.SAVE_COMPETENCE_GENERIQUE_AUTO_EVAL_FAIL, "Erreur auto-evaluation competence generique");
    });
    yield takeLatest(actions.SAVE_COMPETENCE_GENERIQUE_MANAGER_EVAL_REQUEST, function* (action) {
        yield call(mutateCompetenceGeneriqueSaga, action, performanceApi.saveCompetenceGeneriqueManagerEvaluation, actions.SAVE_COMPETENCE_GENERIQUE_MANAGER_EVAL_SUCCESS, actions.SAVE_COMPETENCE_GENERIQUE_MANAGER_EVAL_FAIL, "Erreur evaluation manager competence generique");
    });
    yield takeLatest(actions.SAVE_ALL_COMPETENCES_GENERIQUES_AUTO_EVAL_REQUEST, function* (action) {
        yield call(mutateCompetencesBulkSaga, action, performanceApi.saveCompetenceGeneriqueAutoEvaluation, actions.SAVE_ALL_COMPETENCES_GENERIQUES_AUTO_EVAL_SUCCESS, actions.SAVE_ALL_COMPETENCES_GENERIQUES_AUTO_EVAL_FAIL, refreshCompetencesGeneriques, "Evaluations enregistrees avec succes", "Erreur auto-evaluation competences generiques");
    });
    yield takeLatest(actions.SAVE_ALL_COMPETENCES_GENERIQUES_MANAGER_EVAL_REQUEST, function* (action) {
        yield call(mutateCompetencesBulkSaga, action, performanceApi.saveCompetenceGeneriqueManagerEvaluation, actions.SAVE_ALL_COMPETENCES_GENERIQUES_MANAGER_EVAL_SUCCESS, actions.SAVE_ALL_COMPETENCES_GENERIQUES_MANAGER_EVAL_FAIL, refreshCompetencesGeneriques, "Evaluations validees par le manager", "Erreur evaluation manager competences generiques");
    });

    yield takeLatest(actions.FETCH_COMPETENCES_SPECIFIQUES_STEP_REQUEST, fetchCompetencesSpecifiquesStepSaga);
    yield takeLatest(actions.SAVE_COMPETENCE_SPECIFIQUE_AUTO_EVAL_REQUEST, function* (action) {
        yield call(mutateCompetenceSpecifiqueSaga, action, performanceApi.saveCompetenceSpecifiqueAutoEvaluation, actions.SAVE_COMPETENCE_SPECIFIQUE_AUTO_EVAL_SUCCESS, actions.SAVE_COMPETENCE_SPECIFIQUE_AUTO_EVAL_FAIL, "Erreur auto-evaluation competence specifique");
    });
    yield takeLatest(actions.SAVE_COMPETENCE_SPECIFIQUE_MANAGER_EVAL_REQUEST, function* (action) {
        yield call(mutateCompetenceSpecifiqueSaga, action, performanceApi.saveCompetenceSpecifiqueManagerEvaluation, actions.SAVE_COMPETENCE_SPECIFIQUE_MANAGER_EVAL_SUCCESS, actions.SAVE_COMPETENCE_SPECIFIQUE_MANAGER_EVAL_FAIL, "Erreur evaluation manager competence specifique");
    });
    yield takeLatest(actions.SAVE_ALL_COMPETENCES_SPECIFIQUES_AUTO_EVAL_REQUEST, function* (action) {
        yield call(mutateCompetencesBulkSaga, action, performanceApi.saveCompetenceSpecifiqueAutoEvaluation, actions.SAVE_ALL_COMPETENCES_SPECIFIQUES_AUTO_EVAL_SUCCESS, actions.SAVE_ALL_COMPETENCES_SPECIFIQUES_AUTO_EVAL_FAIL, refreshCompetencesSpecifiques, "Auto-evaluations enregistrees avec succes", "Erreur auto-evaluation competences specifiques", true);
    });
    yield takeLatest(actions.SAVE_ALL_COMPETENCES_SPECIFIQUES_MANAGER_EVAL_REQUEST, function* (action) {
        yield call(mutateCompetencesBulkSaga, action, performanceApi.saveCompetenceSpecifiqueManagerEvaluation, actions.SAVE_ALL_COMPETENCES_SPECIFIQUES_MANAGER_EVAL_SUCCESS, actions.SAVE_ALL_COMPETENCES_SPECIFIQUES_MANAGER_EVAL_FAIL, refreshCompetencesSpecifiques, "Competences specifiques validees par le manager", "Erreur evaluation manager competences specifiques", true);
    });

    yield takeLatest(actions.FETCH_PLAN_DEVELOPPEMENT_STEP_REQUEST, fetchPlanDeveloppementStepSaga);
    yield takeLatest(actions.GENERATE_PLAN_DEVELOPPEMENT_RECOMMENDATIONS_REQUEST, function* (action) {
        yield call(mutatePlanDeveloppementSaga, action, performanceApi.generatePlanDeveloppementRecommendations, actions.GENERATE_PLAN_DEVELOPPEMENT_RECOMMENDATIONS_SUCCESS, actions.GENERATE_PLAN_DEVELOPPEMENT_RECOMMENDATIONS_FAIL, "Erreur generation recommandations");
    });
    yield takeLatest(actions.CREATE_PLAN_DEVELOPPEMENT_ITEM_REQUEST, function* (action) {
        yield call(mutatePlanDeveloppementSaga, action, performanceApi.createPlanDeveloppementItem, actions.CREATE_PLAN_DEVELOPPEMENT_ITEM_SUCCESS, actions.CREATE_PLAN_DEVELOPPEMENT_ITEM_FAIL, "Erreur creation element plan");
    });
    yield takeLatest(actions.CREATE_DEMANDE_FORMATION_FROM_PLAN_REQUEST, function* (action) {
        yield call(mutatePlanDeveloppementSaga, action, performanceApi.createDemandeFormationFromPlan, actions.CREATE_DEMANDE_FORMATION_FROM_PLAN_SUCCESS, actions.CREATE_DEMANDE_FORMATION_FROM_PLAN_FAIL, "Erreur demande formation");
    });
    yield takeLatest(actions.COMPLETE_PLAN_DEVELOPPEMENT_STEP_REQUEST, function* (action) {
        yield call(mutatePlanDeveloppementSaga, action, performanceApi.completePlanDeveloppementStep, actions.COMPLETE_PLAN_DEVELOPPEMENT_STEP_SUCCESS, actions.COMPLETE_PLAN_DEVELOPPEMENT_STEP_FAIL, "Erreur completion plan");
    });

    yield takeLatest(actions.FETCH_ENTRETIEN_ANNUEL_REQUEST, fetchEntretienAnnuelSaga);
    yield takeLatest(actions.PLANIFIER_ENTRETIEN_ANNUEL_REQUEST, function* (action) {
        yield call(mutateEntretienSaga, action, performanceApi.planifierEntretienAnnuel, actions.PLANIFIER_ENTRETIEN_ANNUEL_SUCCESS, actions.PLANIFIER_ENTRETIEN_ANNUEL_FAIL, "Erreur planification entretien");
    });
    yield takeLatest(actions.REPLANIFIER_ENTRETIEN_ANNUEL_REQUEST, function* (action) {
        yield call(mutateEntretienSaga, action, performanceApi.replanifierEntretienAnnuel, actions.REPLANIFIER_ENTRETIEN_ANNUEL_SUCCESS, actions.REPLANIFIER_ENTRETIEN_ANNUEL_FAIL, "Erreur replanification entretien");
    });
    yield takeLatest(actions.CONFIRMER_ENTRETIEN_ANNUEL_REQUEST, function* (action) {
        yield call(mutateEntretienSaga, action, performanceApi.confirmerEntretienAnnuel, actions.CONFIRMER_ENTRETIEN_ANNUEL_SUCCESS, actions.CONFIRMER_ENTRETIEN_ANNUEL_FAIL, "Erreur confirmation entretien");
    });
    yield takeLatest(actions.ANNULER_ENTRETIEN_ANNUEL_REQUEST, function* (action) {
        yield call(mutateEntretienSaga, action, performanceApi.annulerEntretienAnnuel, actions.ANNULER_ENTRETIEN_ANNUEL_SUCCESS, actions.ANNULER_ENTRETIEN_ANNUEL_FAIL, "Erreur annulation entretien");
    });
    yield takeLatest(actions.TERMINER_ENTRETIEN_ANNUEL_REQUEST, function* (action) {
        yield call(mutateEntretienSaga, action, performanceApi.terminerEntretienAnnuel, actions.TERMINER_ENTRETIEN_ANNUEL_SUCCESS, actions.TERMINER_ENTRETIEN_ANNUEL_FAIL, "Erreur terminaison entretien");
    });

    yield takeLatest(actions.FETCH_MANAGER_PERFORMANCE_DASHBOARD_REQUEST, fetchManagerDashboardSaga);
    yield takeLatest(actions.FETCH_CONSOLIDATION_CAMPAGNE_REQUEST, fetchCampagneConsolidationSaga);
    yield takeLatest(actions.FETCH_PROGRESS_CONSOLIDATION_REQUEST, fetchProgressConsolidationSaga);
    yield takeLatest(actions.RECALCULATE_CONSOLIDATION_CAMPAGNE_REQUEST, recalculateCampagneConsolidationSaga);
    yield takeLatest(actions.RECALCULATE_PROGRESS_CONSOLIDATION_REQUEST, recalculateProgressConsolidationSaga);
}
